<template>
    <div>
        <v-row no-gutters>
            <v-col cols="12" class="pb-5 pt-2 title">
                {{ account_id ? $t("Change account details") : $t("Create company account") }}
            </v-col>
        </v-row>
        <v-row no-gutters>
            <v-col>
                <v-card class="pa-10">
                    <v-form ref="form" v-model="valid">
                        <v-container fluid>
                            <v-row no-gutters>
                                <v-col cols="12">
                                    <CompanySettingsDisabledWarning v-if="!canEditOwnerLockedSettings"/>
                                </v-col>
                                <v-col cols="12">
                                    <v-row no-gutters>
                                        <v-col cols="10" class="pa-1">
                                            <v-combobox
                                                v-model="account_form.name"
                                                item-text="name"
                                                outlined
                                                :rules="[v => !!v || $t('Name is required')]"
                                                validate-on-blur
                                                required
                                                :items="entries"
                                                :label="$t('Name')"
                                                prepend-inner-icon="search"
                                                :append-icon="null"
                                                :disabled="!canEditOwnerLockedSettings"
                                                :search-input.sync="search"
                                                :loading="isLoading"
                                                no-filter
                                                hide-no-data
                                                return-object
                                                @input="selected"
                                            />
                                        </v-col>
                                        <v-col cols="2" class="pa-1">
                                            <v-row no-gutters justify="end" align="center">
                                                <AccountAvatar
                                                    text-color="neutral_n300"
                                                    text-class="font-weight-medium"
                                                    :color="account_form.avatar ? 'white' : 'neutral_n40'"
                                                    :edit="true"
                                                    :size="100"
                                                    :disabled="!canEditOwnerLockedSettings"
                                                    :image-path="account_form.avatar"
                                                    @changed="account_form.avatar_data = $event"
                                                />
                                            </v-row>
                                        </v-col>
                                    </v-row>
                                    <v-row no-gutters>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.append_settings.reg_code"
                                                color="primary"
                                                outlined
                                                :disabled="!canEditOwnerLockedSettings"
                                                :label="$t('Company number')"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.append_settings.vat_code"
                                                color="primary"
                                                :label="$t('VAT number')"
                                                :disabled="!!account_form.is_paid_standard || !canEditOwnerLockedSettings"
                                                outlined
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-select
                                                v-model="account_form.address_country_code"
                                                outlined
                                                :items="countries"
                                                :rules="[v => !!v || $t('Country is required')]"
                                                validate-on-blur
                                                required
                                                color="primary"
                                                item-text="name"
                                                item-value="code"
                                                :label="$t('Country')"
                                                :disabled="!!account_form.is_paid_standard || !canEditOwnerLockedSettings"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-col cols="12">
                                    <v-row no-gutters>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.address_street"
                                                color="primary"
                                                :label="$t('Address')"
                                                outlined
                                                :disabled="!canEditOwnerLockedSettings"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.address_city"
                                                color="primary"
                                                :label="$t('City')"
                                                outlined
                                                :disabled="!canEditOwnerLockedSettings"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.address_zip"
                                                color="primary"
                                                :label="$t('Postal code')"
                                                outlined
                                                :disabled="!canEditOwnerLockedSettings"
                                            />
                                        </v-col>

                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.address_state"
                                                color="primary"
                                                :label="$t('State')"
                                                :disabled="!!account_form.is_paid_standard || !canEditOwnerLockedSettings"
                                                outlined
                                            />
                                        </v-col>

                                        <v-col cols="4" class="pa-1">
                                            <v-select
                                                v-model="account_form.area_id"
                                                outlined
                                                color="primary"
                                                :items="translatedBusinessAreas"
                                                item-text="name_key"
                                                item-value="id"
                                                :label="$t('Field')"
                                                persistent-hint
                                                :disabled="!canEditOwnerLockedSettings"
                                                :hint="$t('Uku is set up according to the field of work.')"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.append_settings.team_size"
                                                outlined
                                                color="primary"
                                                :disabled="!canEditOwnerLockedSettings"
                                                :label="$t('Size of the team')"
                                            />
                                        </v-col>

                                        <v-col
                                            cols="4"
                                            class="pa-1"
                                        >
                                            <v-select
                                                v-model="account_form.locale_code"
                                                outlined
                                                color="primary"
                                                :items="ui_languages"
                                                :item-text="(item) => formatLanguageLabel(item)"
                                                item-value="code"
                                                :disabled="!canEditOwnerLockedSettings"
                                                :label="$t('Language')"
                                            />
                                        </v-col>

                                        <v-col cols="4" class="pa-1">
                                            <dropdown
                                                :selected="timezones.filter(x => x.value === account_form.timezone)"
                                                :items="timezones"
                                                name-key="title"
                                                value-key="value"
                                                show-selected-first
                                                :disabled="!canEditOwnerLockedSettings"
                                                :label="$t('Timezone')"
                                                @input="account_form.timezone = $event.value"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.append_settings.accountant_email"
                                                color="primary"
                                                :label="$t('Invoice email')"
                                                outlined
                                                :disabled="!canEditOwnerLockedSettings"
                                            />
                                        </v-col>
                                        <v-col cols="4" class="pa-1">
                                            <v-text-field
                                                v-model="account_form.website"
                                                color="primary"
                                                :label="$t('Website')"
                                                outlined
                                            />
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-form>
                    <v-card-actions class="px-3">
                        <danger-button
                            v-if="can_delete_account"
                            @click="onDeleteCompanyClick"
                        >
                            {{ $t("Delete account") }}
                        </danger-button>
                        <v-spacer/>
                        <secondary-button
                            @click="onCancelClick">
                            {{ $t("Cancel") }}
                        </secondary-button>
                        <primary-button
                            :loading="loading"
                            :disabled="!canEditOwnerLockedSettings"
                            @click="onSaveClick"
                        >
                            {{ $t("Save") }}
                        </primary-button>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import errorDialog from "@/views/ErrorDialog/errorDialog"
import {companies} from "@/api";
import {throttle} from "throttle-debounce";
import {$t} from "@/i18n";
import {computed, watch, reactive, toRefs, ref, onMounted} from "@vue/composition-api";
import AccountModule from "@/store/modules/account";
import AppModule from "@/store/modules/app";
import DialogModule from "@/store/modules/dialog";
import AccountAvatar from "@/components/AccountAvatar";
import {Router} from "@/composables/Router";
import BusinessAreaModule from "@/modules/classificator/businessArea";
import {APP_STORE_APP_TYPES} from "@/helpers/constants";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import CompanySettingsDisabledWarning from "@/components/Company/CompanySettingsDisabledWarning";
import RightsModule from "@/composables/use-rights";
import {uku_send_event, UKU_ACTIVITIES} from "@/helpers/uku_activity";
import {formatLanguageLabel} from '@/helpers/formatting'

export default {
    name: "CompanyEditView",
    components: {
        AccountAvatar,
        CompanySettingsDisabledWarning
    },
    setup(props, ctx) {
        const {can_edit_owner_locked_settings} = RightsModule();
        const form = ref(null);
        const {openConfirmDialog, setConfirmOptions} = DialogModule();
        const router = Router();
        const {appStoreDialogActions} = appStoreDialog()
        const {deleteAccount, getAccount, current_account: personal_account, fetchSettingsState} = AccountModule();
        const {putAccount} = AccountModule();
        const account_id = computed(() => {
            return ctx.root._route.params.id ? Number(ctx.root._route.params.id) : null;
        });
        const current_account = getAccount(account_id);
        const {
            countries,
            ui_languages,
            business_areas,
            fetchClassifiers,
            fetchInitialData,
            timezones,
            fetchTimezones
        } = AppModule();

        const state = reactive({
            valid: false,
            account_form: {
                name: "",
                address_country_code: "",
                address_city: "",
                address_street: "",
                address_state: "",
                address_zip: "",
                locale_code: "",
                timezone: "",
                avatar: "",
                area_id: "",
                website: "",
                append_settings: {
                    reg_code: "",
                    vat_code: "",
                    auto_fill_task_duration: "",
                    checklist_completed: "",
                    team_size: "",
                    topic_management_disabled_for_users: "",
                    time_management_disabled_for_users: "",
                    is_document_admin_only: "",
                    is_digitization_enabled: "",
                    client_management_disabled_for_users: "",
                    accountant_name: "",
                    accountant_email: "",
                }
            },
            entries: [],
            isLoading: false
        });
        const loading = ref(false);

        const can_delete_account = computed(() => {
            return current_account.value && current_account.value.members.find(x => x.role === 'owner' && x.id === personal_account.value.id)
        });

        const canEditOwnerLockedSettings = computed(() => current_account.value ? can_edit_owner_locked_settings(current_account.value) : true)

        const init = async () => {
            let account = current_account.value ? current_account.value : personal_account.value;
            console.log("current account", current_account)

            //if has current_account, is company account edit, else clone personal data for company account creation
            await fetchClassifiers({
                types: ["countries", "languages", "business_areas"],
                account_id: account.id
            });
            state.account_form = Object.assign({}, state.account_form, {
                name: account.type === "company" ? account.name : "",
                address_country_code: account.address_country_code,
                address_city: account.address_city,
                address_street: account.address_street,
                address_state: account.address_state,
                address_zip: account.address_zip,
                locale_code: account.locale_code,
                timezone: account.timezone,
                avatar: account.type === "company" ? account.avatar : "",
                area_id: account.area_id,
                website: account.website,
                append_settings: {
                    reg_code: account.data.reg_code,
                    vat_code: account.data.vat_code,
                    auto_fill_task_duration: account.data.auto_fill_task_duration,
                    checklist_completed: account.data.checklist_completed,
                    team_size: account.data.team_size,
                    topic_management_disabled_for_users: account.data.topic_management_disabled_for_users,
                    time_management_disabled_for_users: account.data.time_management_disabled_for_users,
                    is_document_admin_only: account.data.is_document_admin_only,
                    is_digitization_enabled: account.data.is_digitization_enabled,
                    client_management_disabled_for_users: account.data.client_management_disabled_for_users,
                    accountant_name: account.data.accountant_name,
                    accountant_email: account.data.accountant_email,
                }
            });
        };
        init();

        onMounted(async () => {
            await fetchTimezones()
        })

        const onSaveClick = async () => {
            loading.value = true;
            await form.value.validate();

            if (!state.valid) {
                loading.value = false;
                return;
            }
            if (account_id.value) {
                state.account_form.id = account_id.value;
                // TODO TRACK "company_updated"
            } else {
                // TODO TRACK "company_added"
            }
            putAccount(state.account_form)
                .then(function (saved_account) {
                    Promise.all([fetchInitialData(true), fetchSettingsState()])
                        .then(() => {
                            router.push({name: "app_store", params: {accountId: saved_account.id}});
                            appStoreDialogActions.openAppStoreModal(
                                APP_STORE_APP_TYPES.COMPANY,
                                saved_account.id,
                                {
                                    dialogOptions: {defaultTab: 'overview'},
                                    onUpdate: () => {
                                    }
                                }
                            )
                        })
                        .catch(() => {
                            console.error("CompanyEdit :: onSaveClick :: Failed to fetch initialData or settingsState");
                        })
                        .finally(() => {
                            loading.value = false;
                        });
                })
                .catch(ex => {
                    loading.value = false;
                    errorDialog.showError(ex);
                });
        };
        const autocomplete = async search => {
            state.account_form.name = search;
            const country_code = state.account_form.address_country_code
            try {
                const response = await companies.search({keyword: search, country_code: country_code});
                state.entries = response.results;
            } catch (error) {
                console.error("Company autocomplete", error);
            }
            return true;
        };

        const throttleAutocomplete = throttle(350, function (val) {
            autocomplete(val);
        });
        const selected = data => {
            if (data) {
                if (typeof data === "string") {
                    return (state.account_form.name = data);
                } else if (data instanceof Object) {
                    const {append_settings} = state.account_form;
                    append_settings.reg_code = data.reg_no;
                    append_settings.vat_code = data.eu_vat_no;
                    state.account_form = Object.assign({}, state.account_form, {
                        append_settings: append_settings,
                        address_city: data.city,
                        address_street: data.address,
                        address_zip: data.zip_code,
                        name: data.name
                    });
                }
            }
        };
        const translatedBusinessAreas = computed(() => {
            let ret = []
            for (let item of business_areas.value) {
                item.name_key = BusinessAreaModule.translateBusinessArea(item.name_key)

                ret.push(item)
            }

            return ret
        })

        const onDeleteCompanyConfirm = async () => {
            deleteAccount(current_account.value.id)
                .then(() => {
                    uku_send_event({event: UKU_ACTIVITIES.COMPANY_DELETED, info: "Company/Index.onDeleteCompanyConfirm"})
                    router.push({name: "app_store"});
                })
                .catch(ex => errorDialog.showError(ex));
        };

        const onCancelClick = () => {
            if (account_id.value) {
                appStoreDialogActions.openAppStoreModal(
                    APP_STORE_APP_TYPES.COMPANY,
                    account_id.value,
                    {
                        dialogOptions: {defaultTab: 'overview'},
                        onUpdate: () => {
                        }
                    }
                )
            }
            router.push({name: "app_store", params: {accountId: account_id.value}});
        }

        const onDeleteCompanyClick = () => {
            setConfirmOptions({
                title: $t("Attention"),
                text: $t(
                    "Deleting an account will also delete all clients associated with the account, their tasks, and their entire history. Are you sure that you want to delete your account?"
                ),
                options: [
                    {
                        name: "confirm_deletion",
                        text: $t(
                            "Yes, delete the account with all related data such as clients, tasks, and all history"
                        ),
                        checked: false,
                        hints: false,
                        mandatory: true
                    }
                ],
                confirm_text: $t("Confirm"),
                cancel_text: $t("Cancel"),
                action: onDeleteCompanyConfirm
            });
            openConfirmDialog();
        };
        const search = ref(null);
        watch(search, val => {
            return val && val !== state.account_form.name && throttleAutocomplete(val);
        });

        return {
            ...toRefs(state),
            account_id,
            form,
            current_account,
            search,
            loading,
            can_delete_account,
            onSaveClick,
            throttleAutocomplete,
            countries,
            ui_languages,
            selected,
            translatedBusinessAreas,
            onDeleteCompanyClick,
            onCancelClick,
            canEditOwnerLockedSettings,
            timezones,
            formatLanguageLabel
        };
    }
};
</script>
